import { useState, type JSX } from "react";
import { Button, Form, InputGroup } from "react-bootstrap";

type PasswordInputProps = {
  id?: string;
  value: string;
  onChange: (value: string) => void;
  error?: string;
  placeholder?: string;
  disabled?: boolean;
  autoComplete?: string;
  autoFocus?: boolean;
};

/**
 * Password field with a show/hide (eye) toggle. Visibility resets whenever
 * the component unmounts (e.g. its modal closes).
 */
export default function PasswordInput({
  id,
  value,
  onChange,
  error,
  placeholder = "Enter password",
  disabled = false,
  autoComplete = "new-password",
  autoFocus = false,
}: PasswordInputProps): JSX.Element {
  const [visible, setVisible] = useState(false);

  return (
    <InputGroup hasValidation>
      <Form.Control
        id={id}
        type={visible ? "text" : "password"}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        isInvalid={!!error}
        placeholder={placeholder}
        disabled={disabled}
        autoComplete={autoComplete}
        autoFocus={autoFocus}
      />
      <Button
        variant="outline-secondary"
        onClick={() => setVisible((v) => !v)}
        disabled={disabled}
        aria-label={visible ? "Hide password" : "Show password"}
        aria-pressed={visible}
        title={visible ? "Hide password" : "Show password"}
      >
        <i
          className={`bi ${visible ? "bi-eye-slash" : "bi-eye"}`}
          aria-hidden="true"
        />
      </Button>
      <Form.Control.Feedback type="invalid">{error}</Form.Control.Feedback>
    </InputGroup>
  );
}
